import { ASSIGNMENT_METHODS, INCOTERMS_CODES } from "./newOrderPayload";
import { isRailwayOrder } from "./railwayOrder";

// Shipment dates are calendar dates; tender deadlines are instants in the user's timezone.
export const parseShipmentDate = (value) => {
  if (!value) return null;
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const [, year, month, day] = match.map(Number);
  const date = new Date(year, month - 1, day);
  return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day ? date : null;
};

export const parseOrderHour = (value) => {
  if (value == null || value === "") return null;
  const hour = Number(value);
  return Number.isInteger(hour) && hour >= 0 && hour <= 23 ? new Date(2000, 0, 1, hour) : null;
};

const option = (items = [], key, value, name) =>
  items.find((item) => item[key] === value) ?? (value == null ? null : { [key]: value, name: name ?? String(value) });

export const parseOrderForm = (
  { order, cargo = {}, tenderPartners = [], documents = [], invoices = [] },
  directories,
  { isCopy = false } = {},
) => {
  const isRailway = isRailwayOrder(order);
  const { countries = [], categories = [], types = [], temperatures = [], loadingTypes = [],
    partners = [], citiesByCountry = {}, weightUnits, wagonTypes = [] } = directories;
  const addresses = [...(order.addressList ?? [])].sort((a, b) => (a.step ?? 0) - (b.step ?? 0));
  const first = addresses[0] ?? {};
  const last = addresses.length > 1 ? addresses[addresses.length - 1] : {};
  const countryIdFrom = first.countryId ?? order.countryIdFrom;
  const countryIdTo = last.countryId ?? order.countryIdTo;
  const cargoData = { ...order, ...cargo };
  const assignmentMethod = ASSIGNMENT_METHODS.includes(order.assignmentMethod) ? order.assignmentMethod : null;
  if (!assignmentMethod) throw new Error("Missing or invalid assignmentMethod");
  const isComplexTender = assignmentMethod !== "DirectAssignment" && Boolean(order.isComplexTender);
  const deadline = order.tenderDateEnd ? new Date(order.tenderDateEnd) : null;
  const tenderDate = deadline && !Number.isNaN(deadline.getTime()) ? deadline : null;
  const partnerOption = (entry) => {
    const id = typeof entry === "string" ? entry : entry.expeditorCompanyId ?? entry.expeditorId ?? entry.id;
    return partners.find((partner) => partner.expeditorCompanyId === id) ?? {
      ...(typeof entry === "object" ? entry : {}),
      expeditorCompanyId: id,
      name: entry.companyName ?? entry.name ?? id,
    };
  };
  const incoterms = countryIdFrom && countryIdTo && countryIdFrom !== countryIdTo &&
    INCOTERMS_CODES.includes(order.incoterms) ? order.incoterms : null;

  return {
    // Keep fields without a form control (IDs, status, dimensions, etc.) for the future API contract.
    source: { order, cargo, tenderPartners, documents, invoices },
    countries, categories, types, temperatures, loadingTypes, partners, wagonTypes,
    transportationType: order.transportationType,
    isRailway,
    wagonType: option(wagonTypes, "id", order.wagonTypeId, order.wagonTypeName),
    wagonsCount: isRailway && isComplexTender ? "" : order.wagonsCount ?? "",
    etsngCode: order.etsngCode ?? "",
    gngCode: order.gngCode ?? "",
    countryFrom: option(countries, "id", countryIdFrom, first.countryName ?? order.countryFromName),
    countryTo: option(countries, "id", countryIdTo, last.countryName ?? order.countryToName),
    citiesFrom: citiesByCountry[countryIdFrom] ?? [],
    citiesTo: citiesByCountry[countryIdTo] ?? [],
    cityFrom: option(citiesByCountry[countryIdFrom], "id", first.cityId ?? order.cityIdFrom, first.cityName ?? order.cityFromName),
    cityTo: option(citiesByCountry[countryIdTo], "id", last.cityId ?? order.cityIdTo, last.cityName ?? order.cityToName),
    addressFrom: first.address ?? order.addressFrom ?? "",
    addressTo: last.address ?? order.addressTo ?? "",
    addressIdFrom: first.userAddressId ?? first.addressId ?? "",
    addressIdTo: last.userAddressId ?? last.addressId ?? "",
    coordinatesFrom: first.latLng ?? "",
    coordinatesTo: last.latLng ?? order.latLng ?? "",
    pickUpDate: parseShipmentDate(first.shippingDate ?? order.shippingDate),
    shippingDateTo: parseShipmentDate(order.shippingDateTo),
    pickUpStartTime: parseOrderHour(first.shippingHourFrom ?? order.shippingHourFrom),
    pickUpEndTime: parseOrderHour(first.shippingHourTo ?? order.shippingHourTo),
    deliveryDate: parseShipmentDate(last.shippingDate ?? order.unloadingDate),
    deliveryStartTime: parseOrderHour(last.shippingHourFrom ?? order.unloadingHourFrom),
    deliveryEndTime: parseOrderHour(last.shippingHourTo ?? order.unloadingHourTo),
    senderName: first.senderName ?? first.recipientName ?? order.senderName ?? "",
    senderPhone: first.senderPhoneNumber ?? first.recipientPhoneNumber ?? order.senderPhoneNumber ?? "",
    senderEmail: first.senderEmail ?? first.recipientEmail ?? "",
    recipientName: last.recipientName ?? order.recipientName ?? "",
    recipientPhone: last.recipientPhoneNumber ?? order.recipientPhoneNumber ?? "",
    recipientEmail: last.recipientEmail ?? order.recipientEmail ?? "",
    points: addresses.slice(1, -1).map((point, index) => {
      const loading = point.type === "Loading" || point.type === 0;
      return {
        ...point,
        country: option(countries, "id", point.countryId, point.countryName),
        city: option(citiesByCountry[point.countryId], "id", point.cityId, point.cityName),
        cities: citiesByCountry[point.countryId] ?? [],
        tempCityId: point.cityId,
        addressId: point.userAddressId ?? point.addressId ?? "",
        type: loading ? 0 : 1,
        step: point.step ?? index + 2,
        isInterimPoint: true,
        shippingDate: parseShipmentDate(point.shippingDate),
        shippingHourFrom: parseOrderHour(point.shippingHourFrom),
        shippingHourTo: parseOrderHour(point.shippingHourTo),
        recipientName: (loading ? point.senderName ?? point.recipientName : point.recipientName) ?? "",
        recipientPhoneNumber: (loading ? point.senderPhoneNumber ?? point.recipientPhoneNumber : point.recipientPhoneNumber) ?? "",
        recipientEmail: (loading ? point.senderEmail ?? point.recipientEmail : point.recipientEmail) ?? "",
      };
    }),
    type: option(types, "value", cargoData.vehicleType),
    category: option(categories, "value", cargoData.category),
    loadingType: option(loadingTypes, "id", cargoData.loadingTypeId),
    temperature: cargoData.temperature ?? "",
    weight: cargoData.weight ?? "",
    volume: cargoData.volume ?? "",
    pallet: cargoData.pallet ?? "",
    weightUnit: Object.values(weightUnits).find((unit) => unit.code === cargoData.unitMeasurement) ?? weightUnits.ton,
    details: cargoData.details ?? "",
    tnvedCode: order.tnvedCode ?? cargo.tnvedCode ?? "",
    price: order.price ?? 0,
    netPrice: order.netPrice ?? 0,
    algoPrice: order.algoPrice ?? 0,
    isAlgoPrice: order.isAlgoPrice ?? false,
    distance: order.distance ?? 0,
    routeDistance: order.distance ?? 0,
    isActualCities: order.isActualCities ?? true,
    insurance: order.insuranceIncluded ?? false,
    declaredPrice: order.cargoPrice ?? order.declaredPrice ?? 0,
    insurancePrice: order.insuranceSum ?? order.insurancePrice ?? 0,
    currency: order.currency ?? null,
    acceptedCurrency: order.acceptedCurrency ?? null,
    assignmentMethod,
    auctionStep: order.bidReductionStep ?? "",
    extendAuction: order.isAuctionExtended ?? false,
    partner: !isCopy && order.expeditorId ? partnerOption({ id: order.expeditorId, name: order.expeditorName }) : null,
    partnersForTender: isCopy ? [] : tenderPartners.filter(Boolean).map(partnerOption),
    tenderDateEnd: tenderDate,
    tenderEndTime: tenderDate,
    isComplexTender,
    vehiclesCount: order.vehiclesCount ?? 1,
    isWithoutExecutor: order.isWithoutExecutor ?? false,
    incoterms,
    incotermsEnabled: incoterms !== null,
    serverFiles: documents,
    fileInvoice: invoices[0] ? { ...invoices[0], name: invoices[0].fileName ?? invoices[0].name } : null,
  };
};
